<template>
  <div class="relative h-full f-col" :class="{ 'bg-white': whiteBackground }">
    <base-button
      v-if="quickshop"
      :aria-describedby="id"
      :aria-label="$t.add"
      class="group absolute right-0 top-0 z-3 p-1"
      data-test-id="product-card-add"
      @click="PanelQuickshop.open({ product, closeOnAdd: closeQuickshopOnAdd, gtmConfig })"
    >
      <span class="block rounded-xs p-1 duration lg:m-2 group-hover:bg-brand-2">
        <vf-icon :height="{ sm: '20', lg: '24' }" name="plus" :width="{ sm: '20', lg: '24' }" />
      </span>
    </base-button>
    <base-picture
      :alt="image.alt"
      v-bind="size"
      class="w-full"
      :src="getResponsiveImageTransformations(image.src, {
        ...size,
        ...transformations,
      })"
    />
    <div v-style:c="textColor" class="f-col gap-1 p-2">
      <base-link
        :id
        class="line-clamp-2 self-start subtitle-4"
        :to="{
          path: url,
          state: searchType ? { category: `Search: ${searchType}`, searchType, suggestedPhrase } : {},
        }"
        @click="setInteractionOrigin('search')"
      >
        {{ title }}
        <span class="absolute-0" />
      </base-link>
      <product-pricing
        class="subtitle-5"
        compact-omnibus
        :currency
        :price
        :show-discount-percentage="$feature.showDiscountPercentageOnCatalog"
        show-dynamic-pricing-on-plp
      />
    </div>
    <panel-quickshop />
  </div>
</template>

<script lang="ts" setup>
import type { RecommendationStyle } from '#content/contexts/recommendation'
import type { ProductVariants } from '#root/api/clients/product/data-contracts'
import type { ProductContext } from '#types/gtm'
import type { ImageItem } from '#types/media'
import type { PriceFull } from '#types/product'
import type { HistoryState } from 'vue-router'
import { RecommendationStyleContextKey } from '#content/contexts/recommendation'

const { title, url, image, id, currency, price, quickshop = true } = defineProps<{
  /**
   * Product name or title
   */
  title: string
  /**
   * Path to product detail page
   */
  url: string
  /**
   * Default product image
   */
  image: ImageItem
  /**
   * Product id
   */
  id: string
  /**
   * Product price currency
   */
  currency: string
  /**
   * Product price data
   */
  price: PriceFull
  /**
   * Quickshop Toggle
   */
  quickshop?: boolean
  gtmConfig: ProductContext
}>()

const { presets: { size, transformations } = {} } = useAppConfig().components.cms.productRecommendations
const route = useRoute()

const searchType = inject('searchType', undefined) as HistoryState | undefined
const suggestedPhrase = inject('suggestedPhrase', undefined) as HistoryState | undefined

const product = computed(() => ({
  id,
  name: title,
  url,
  price,
  currency,
  attributes: [],
  variants: {} as ProductVariants,
}))

const closeQuickshopOnAdd = route.name?.toString().includes('cart')

const PanelQuickshop = createPanel('quickshop')

const { textColor, whiteBackground } = inject(RecommendationStyleContextKey, {} as RecommendationStyle)
</script>
